import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import Navbar from '../../components/navbar/navbar'
import Footer from '../../components/footer'
import Slider from 'react-slick'
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"

import { API_BASE, getImageUrl } from '../../utils/api'

interface DBOwner {
    _id: string; name: string; email: string; role: string; phone?: string;
    about?: string; city?: string; state?: string; avatar?: string;
    projects?: string; 
    expertise?: string;
    createdAt?: string;
    facebook?: string; twitter?: string; linkedin?: string;
    projectsSince?: string;
    projectsCompleted?: string;
    projectsOngoing?: string;
    projectsUpcoming?: string;
    totalProjects?: string;
}

interface DBProject {
    _id: string; name: string; location: string; city?: string;
    featuredImage?: string; images?: string[];
    gallery?: { exterior?: string[]; interior?: string[]; amenities?: string[]; construction?: string[] };
    overview?: { configuration?: string; sizeRange?: string; priceRange?: string; totalUnits?: string; };
    projectStatus?: string; status?: string; propertyType?: string;
    shortDescription?: string; description?: string;
    priceRange?: string;
    isActive: boolean;
    slug?: string;
}

export default function OwnerProfile() {
    const [owner, setOwner] = useState<DBOwner | null>(null)
    const [projects, setProjects] = useState<DBProject[]>([])
    const [loading, setLoading] = useState(true)
    const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: "I'm interested in your properties." })

    const params = useParams()
    const id = params.id

    useEffect(() => {
        if (!id) return;
        setLoading(true)
        fetch(`${API_BASE}/users/agents/${id}`)
            .then(r => r.json())
            .then(data => {
                if (data.agent) {
                    setOwner(data.agent)
                    setProjects([...(data.properties || []), ...(data.projects || [])])
                } else if (data.developer) {
                    setOwner(data.developer)
                    setProjects(data.projects || [])
                } else if (data.user) {
                    setOwner(data.user)
                    setProjects(data.properties || data.projects || [])
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [id])

    const getProjectImage = (project: DBProject) => {
        if (project.featuredImage) return getImageUrl(project.featuredImage)
        if (project.gallery?.exterior?.[0]) return getImageUrl(project.gallery.exterior[0])
        if (project.gallery?.interior?.[0]) return getImageUrl(project.gallery.interior[0])
        if (project.images?.[0]) return getImageUrl(project.images[0])
        return '/assets/img/p-1.jpg'
    }

    const getProjectPrice = (project: DBProject) => {
        return project.overview?.priceRange || project.priceRange || 'Price on Request'
    }

    const getProjectStatus = (project: DBProject) => {
        return project.projectStatus || project.status || 'Active'
    }

    if (loading) {
        return (
            <>
                <Navbar transparent={false} />
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status"></div>
                    <p className="mt-3 text-black fw-bold">Loading owner profile...</p>
                </div>
            </>
        )
    }

    if (!owner) {
        return (
            <>
                <Navbar transparent={false} />
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                    <i className="fa-solid fa-user-xmark display-1 text-black opacity-25 mb-3"></i>
                    <h3 className="fw-bold text-black">Owner not found</h3>
                    <p className="text-black fw-bold">The owner you're looking for doesn't exist or has been removed.</p>
                    <Link to="/" className="btn btn-primary rounded-pill px-4 mt-2">
                        <i className="fa-solid fa-arrow-left me-2"></i>Back to Home
                    </Link>
                </div>
            </>
        )
    }

    return (
        <>
            <Navbar transparent={false} />

            {/* Modern Hero Banner */}
            <section className="position-relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, #05144b 0%, #4c1d95 100%)',
                minHeight: '320px'
            }}>
                {/* Decorative Elements */}
                <div className="position-absolute w-100 h-100" style={{ top: 0, left: 0, opacity: 0.06 }}>
                    <div className="position-absolute" style={{ width: '400px', height: '400px', borderRadius: '50%', border: '2px solid white', top: '-100px', right: '-50px' }}></div>
                    <div className="position-absolute" style={{ width: '300px', height: '300px', borderRadius: '50%', border: '2px solid white', bottom: '-80px', left: '-50px' }}></div>
                    <div className="position-absolute" style={{ width: '200px', height: '200px', borderRadius: '50%', border: '1px solid white', top: '50px', left: '30%' }}></div>
                </div>

                <div className="container position-relative" style={{ zIndex: 2, paddingTop: '60px', paddingBottom: '80px' }}>
                    <nav aria-label="breadcrumb" className="mb-4">
                        <ol className="breadcrumb mb-0" style={{ fontSize: '0.85rem' }}>
                            <li className="breadcrumb-item"><Link to="/" className="text-white-50 text-decoration-none">Home</Link></li>
                            <li className="breadcrumb-item text-white fw-medium" aria-current="page">{owner.name}</li>
                        </ol>
                    </nav>
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <div className="bg-white rounded-4 p-2 shadow-lg" style={{ width: '110px', height: '110px' }}>
                                <img
                                    src={getImageUrl(owner.avatar || '/assets/img/user-1.jpg')}
                                    alt={owner.name}
                                    className="img-fluid rounded-3 w-100 h-100"
                                    style={{ objectFit: 'contain' }}
                                    onError={(e: any) => { e.target.src = '/assets/img/user-1.jpg'; }}
                                />
                            </div>
                        </div>
                        <div className="col">
                            <h1 className="text-white fw-bold mb-2" style={{ fontSize: '2rem' }}>{owner.name}</h1>
                            <div className="d-flex flex-wrap gap-3 align-items-center">
                                {owner.city && (
                                    <span className="badge text-white px-3 py-2 rounded-pill" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)' }}>
                                        <i className="fa-solid fa-location-dot me-1"></i>{owner.city}{owner.state ? `, ${owner.state}` : ''}
                                    </span>
                                )}
                                <span className="badge text-white px-3 py-2 rounded-pill" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)' }}>
                                    <i className="fa-solid fa-building me-1"></i>{projects.length} Listing{projects.length !== 1 ? 's' : ''}
                                </span>
                                <span className="badge text-white px-3 py-2 rounded-pill" style={{ background: 'rgba(25,135,84,0.85)' }}>
                                    <i className="fa-solid fa-circle-check me-1"></i>Verified Owner
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-5" style={{ background: '#f8f9fc' }}>
                <div className="container">
                    <div className="row g-4">

                        {/* Left Column - Main Content */}
                        <div className="col-lg-8">

                            {/* About Section */}
                            <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                                <div className="card-header bg-white border-bottom py-3 px-4">
                                    <h5 className="fw-bold mb-0">
                                        <i className="fa-solid fa-user me-2 text-primary"></i>About {owner.name}
                                    </h5>
                                </div>
                                <div className="card-body p-4">
                                    <p className="text-black fw-bold mb-0 lh-lg" style={{ fontSize: '0.95rem' }}>
                                        {owner.about || `${owner.name} is a verified property owner listing high-quality properties for sale and rent.`}
                                    </p>
                                </div>
                            </div>

                            {/* Projects Section */}
                            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                                <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                                    <h5 className="fw-bold text-black mb-0">
                                        <i className="fa-solid fa-house me-2 text-primary"></i>Listings by {owner.name}
                                    </h5>
                                    <span className="badge bg-primary rounded-pill px-3 py-2">{projects.length} Listing{projects.length !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="card-body p-4">
                                    {projects.length > 0 ? (
                                        <>
                                        <style>{`
                                            .owner-project-slider .slick-prev,
                                            .owner-project-slider .slick-next {
                                                top: 50% !important;
                                                transform: translateY(-50%) !important;
                                                z-index: 1;
                                                width: 36px;
                                                height: 36px;
                                            }
                                            .owner-project-slider .slick-prev {
                                                left: -5px !important;
                                            }
                                            .owner-project-slider .slick-next {
                                                right: -5px !important;
                                            }
                                            .owner-project-slider .slick-prev:before,
                                            .owner-project-slider .slick-next:before {
                                                font-size: 18px;
                                                color: #05144b;
                                                opacity: 0.8;
                                            }
                                        `}</style>
                                        <div className="mx-0 owner-project-slider">
                                            <Slider 
                                                dots={true}
                                                infinite={projects.length > 2}
                                                speed={500}
                                                slidesToShow={projects.length >= 2 ? 2 : 1}
                                                slidesToScroll={1}
                                                autoplay={true}
                                                autoplaySpeed={4000}
                                                arrows={true}
                                                responsive={[
                                                    {
                                                        breakpoint: 768,
                                                        settings: {
                                                            slidesToShow: 1,
                                                            slidesToScroll: 1,
                                                            arrows: false
                                                        }
                                                    }
                                                ]}
                                            >
                                                {projects.map((project) => (
                                                    <div className="px-1 py-1" key={project._id}>
                                                        <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-shadow duration-300 border border-slate-100 h-full flex flex-col">
                                                            {/* Image Section */}
                                                            <div className="relative aspect-[4/3] overflow-hidden">
                                                                <Link to={project.slug || project.name ? `/project-details/${project.slug || project._id}` : `/property/${project._id}`}>
                                                                    <img
                                                                        src={getProjectImage(project)}
                                                                        className="w-full h-full object-cover"
                                                                        alt={project.name || (project as any).title}
                                                                        onError={(e: any) => { e.target.src = '/assets/img/p-1.jpg'; }}
                                                                    />
                                                                </Link>
                                                                
                                                                {/* Top Left Badge - NEW / STATUS */}
                                                                <div className="absolute top-4 left-4">
                                                                    <span className="inline-block bg-[#f1ae24] text-[#001b4a] text-[11px] font-black tracking-wide uppercase px-3 py-1 rounded">
                                                                        {getProjectStatus(project) === 'Ready to Move' ? 'NEW' : getProjectStatus(project)}
                                                                    </span>
                                                                </div>
                                                                
                                                                {/* Bottom Left Badge - VERIFIED */}
                                                                <div className="absolute bottom-4 left-4">
                                                                    <div className="bg-[#05144b] text-white text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded border border-white/20 shadow-sm">
                                                                        VERIFIED
                                                                    </div>
                                                                </div>

                                                                {/* Bottom Right Action Buttons */}
                                                                <div className="absolute bottom-4 right-4 flex gap-2">
                                                                    <button className="w-8 h-8 rounded-full bg-black/40 border border-white/50 text-white flex items-center justify-center hover:bg-black/60 transition-colors backdrop-blur-sm">
                                                                        <i className="fa-regular fa-heart text-xs"></i>
                                                                    </button>
                                                                    <button className="w-8 h-8 rounded-full bg-black/40 border border-white/50 text-white flex items-center justify-center hover:bg-black/60 transition-colors backdrop-blur-sm">
                                                                        <i className="fa-solid fa-code-compare text-xs"></i>
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Content Section */}
                                                            <div className="p-5 flex flex-col flex-grow">
                                                                <Link to={project.slug || project.name ? `/project-details/${project.slug || project._id}` : `/property/${project._id}`} className="text-decoration-none">
                                                                    <h4 className="text-[22px] font-bold text-[#05144b] mb-4">
                                                                        {project.name || (project as any).title}
                                                                    </h4>
                                                                </Link>

                                                                {/* 2x2 Grid Info */}
                                                                <div className="grid grid-cols-2 gap-y-3 gap-x-2 mb-6 flex-grow">
                                                                    <div className="flex items-center text-slate-700 text-[15px] font-medium">
                                                                        <i className="fa-solid fa-location-dot w-5 text-center text-[#05144b] mr-1"></i>
                                                                        <span className="truncate">{project.location || (project as any).address}</span>
                                                                    </div>
                                                                    <div className="flex items-center text-slate-700 text-[15px] font-medium">
                                                                        <i className="fa-solid fa-indian-rupee-sign w-5 text-center text-[#05144b] mr-1"></i>
                                                                        <span className="truncate">{getProjectPrice(project)}</span>
                                                                    </div>
                                                                    <div className="flex items-center text-slate-700 text-[15px] font-medium">
                                                                        <i className="fa-solid fa-bed w-5 text-center text-[#05144b] mr-1"></i>
                                                                        <span className="truncate">
                                                                            {(project.overview?.configuration || (project as any).bedrooms) ? (
                                                                                (project.overview?.configuration || `${(project as any).bedrooms} BHK`).toLowerCase().includes('bhk') 
                                                                                    ? (project.overview?.configuration || `${(project as any).bedrooms} BHK`)
                                                                                    : `${project.overview?.configuration} BHK`
                                                                            ) : 'Studio / 1 BHK'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center text-slate-700 text-[15px] font-medium">
                                                                        <i className="fa-solid fa-building w-5 text-center text-[#05144b] mr-1"></i>
                                                                        <span className="truncate">{owner.name}</span>
                                                                    </div>
                                                                </div>

                                                                {/* Footer Section */}
                                                                <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                                                                    <div className="text-[11px] font-bold text-[#05144b] uppercase tracking-wide">
                                                                        ZERO BROKERAGE
                                                                    </div>
                                                                    
                                                                    <Link 
                                                                        to={project.slug || project.name ? `/project-details/${project.slug || project._id}` : `/property/${project._id}`}
                                                                        className="bg-[#f1ae24] text-[#05144b] hover:bg-[#d99c20] hover:text-[#05144b] text-[11px] font-black tracking-wide uppercase px-2.5 py-1.5 rounded transition-colors text-decoration-none"
                                                                    >
                                                                        VIEW DETAILS
                                                                    </Link>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </Slider>
                                        </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-5 text-black">
                                            <i className="fa-solid fa-house-circle-exclamation fa-3x mb-3 opacity-25"></i>
                                            <p className="fw-bold">No active listings found for this owner.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Sidebar - Dynamic */}
                        <div className="col-lg-4">

                            {/* Developer Contact Card */}
                            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4 sticky-sidebar">
                                <div className="text-center p-4" style={{
                                    background: 'linear-gradient(135deg, #05144b 0%, #4c1d95 100%)'
                                }}>
                                    <div className="bg-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3 shadow" style={{ width: '80px', height: '80px', padding: '4px' }}>
                                        <img
                                            src={getImageUrl(owner.avatar || '/assets/img/user-1.jpg')}
                                            alt={owner.name}
                                            className="rounded-circle w-100 h-100"
                                            style={{ objectFit: 'cover' }}
                                            onError={(e: any) => { e.target.src = '/assets/img/user-1.jpg'; }}
                                        />
                                    </div>
                                    <h5 className="text-white fw-bold mb-1">{owner.name}</h5>
                                    {owner.city && (
                                        <p className="text-white-50 small mb-2">
                                            <i className="fa-solid fa-location-dot me-1"></i>{owner.city}
                                        </p>
                                    )}
                                </div>

                                {/* Contact Details */}
                                <div className="p-4">
                                    <h6 className="fw-bold text-black mb-3">
                                        <i className="fa-solid fa-circle-info me-2 text-primary"></i>Owner Info
                                    </h6>
                                    <ul className="list-unstyled mb-0">
                                        {owner.city && (
                                            <li className="d-flex align-items-center py-2 border-bottom">
                                                <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '36px', height: '36px', minWidth: '36px' }}>
                                                    <i className="fa-solid fa-location-dot text-warning" style={{ fontSize: '0.8rem' }}></i>
                                                </div>
                                                <div>
                                                    <small className="text-black fw-bold d-block" style={{ fontSize: '0.7rem' }}>LOCATION</small>
                                                    <span className="text-black fw-bold" style={{ fontSize: '0.85rem' }}>{owner.city}{owner.state ? `, ${owner.state}` : ''}</span>
                                                </div>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            {/* Contact Form */}
                            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                                <div className="card-header bg-white border-bottom py-3 px-4">
                                    <h6 className="fw-bold mb-0">
                                        <i className="fa-solid fa-paper-plane me-2 text-primary"></i>Contact Owner
                                    </h6>
                                </div>
                                <div className="card-body p-4">
                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            className="form-control rounded-3 border-light-subtle bg-light-subtle shadow-none py-2"
                                            placeholder="Your Name"
                                            value={contactForm.name}
                                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <input
                                            type="email"
                                            className="form-control rounded-3 border-light-subtle bg-light-subtle shadow-none py-2"
                                            placeholder="Your Email"
                                            value={contactForm.email}
                                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <input
                                            type="tel"
                                            className="form-control rounded-3 border-light-subtle bg-light-subtle shadow-none py-2"
                                            placeholder="Your Phone"
                                            value={contactForm.phone}
                                            onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <textarea
                                            className="form-control rounded-3 border-light-subtle bg-light-subtle shadow-none py-2"
                                            rows={3}
                                            placeholder="Your Message"
                                            value={contactForm.message}
                                            onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                        ></textarea>
                                    </div>
                                    <button className="btn text-white w-100 rounded-3 fw-bold py-2 shadow-sm" style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}>
                                        <i className="fa-solid fa-paper-plane me-2"></i>Send Message
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <style>{`
                .project-card-modern {
                    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
                    background: white;
                }
                .project-card-modern:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 30px 60px -12px rgba(50, 50, 93, 0.15), 0 18px 36px -18px rgba(0, 0, 0, 0.2) !important;
                }
                .project-card-modern img {
                    transition: transform 0.8s cubic-bezier(0.165, 0.84, 0.44, 1);
                }
                .project-card-modern:hover img {
                    transform: scale(1.08);
                }
                .hover-text-gold:hover {
                    color: #f1ae24 !important;
                }
                .fw-black { font-weight: 900 !important; }
                .text-[#001b4a] { color: #001b4a !important; }
                .hover-translate-y-neg-2:hover {
                    transform: translateY(-2px);
                }
                .shadow-xs {
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                }
                .stat-card {
                    transition: all 0.3s ease;
                }
                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
                }
                .btn-outline-navy { border: 2px solid #05144b; color: #05144b; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); background: transparent; }
                .btn-outline-navy:hover { background: linear-gradient(90deg, #05144b 0%, #4c1d95 100%); border-color: transparent; color: white; transform: translateY(-3px); box-shadow: 0 8px 25px rgba(5, 20, 75, 0.2); }
                .recent-project-item {
                    transition: all 0.2s ease;
                }
                .recent-project-item:hover {
                    background-color: #f0f4ff;
                }
                .sticky-sidebar {
                    position: sticky;
                    top: 90px;
                    z-index: 10;
                }
                @media (max-width: 991px) {
                    .sticky-sidebar {
                        position: static;
                    }
                }
                .bg-gradient-to-t {
                    background: linear-gradient(to top, var(--tw-gradient-stops));
                }
                .from-black\\/40 {
                    --tw-gradient-from: rgb(0 0 0 / 0.4);
                    --tw-gradient-to: rgb(0 0 0 / 0);
                    --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
                }
                @media (min-width: 768px) {
                    .border-start-md {
                        border-left: 1px solid #e2e8f0 !important;
                    }
                }
            `}</style>

            <Footer />
        </>
    )
}
